import { toast } from 'react-toastify';

// Types pour la configuration des toasts
export interface ToastConfig {
  position?: 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center';
  autoClose?: number | false;
  hideProgressBar?: boolean;
  pauseOnHover?: boolean;
  draggable?: boolean;
}

// Interface for CRUD operation messages
export interface CrudMessages {
  load?: {
    success?: string;
    error?: string;
  };
  create?: {
    success?: string;
    error?: string;
  };
  update?: {
    success?: string;
    error?: string;
  };
  delete?: {
    success?: string;
    error?: string;
  };
  toggle?: {
    success?: string;
    error?: string;
  };
  reorder?: {
    success?: string;
    error?: string;
  };
  upload?: {
    success?: string;
    error?: string;
  };
  validation?: {
    error?: string;
  };
}

// Default configuration
const defaultConfig: ToastConfig = {
  position: 'top-right',
  autoClose: 3000,
  hideProgressBar: false,
  pauseOnHover: true,
  draggable: true,
};

// Service de notification toast
export class ToastService {
  private static config: ToastConfig = defaultConfig;

  // Configuration globale
  static configure(config: Partial<ToastConfig>) {
    this.config = { ...this.config, ...config };
  }

  // Base methods
  static success(message: string, config?: Partial<ToastConfig>) {
    toast.success(message, { ...this.config, ...config });
  }

  static error(message: string, config?: Partial<ToastConfig>) {
    toast.error(message, { ...this.config, ...config });
  }

  static info(message: string, config?: Partial<ToastConfig>) {
    toast.info(message, { ...this.config, ...config });
  }

  static warning(message: string, config?: Partial<ToastConfig>) {
    toast.warning(message, { ...this.config, ...config });
  }

  // Helpers for CRUD operations
  static crud = {
    // Chargement
    // Volontairement silencieux : les notifications « chargé(e)s avec succès »
    // sont du bruit dans le dashboard admin. On conserve la méthode pour ne pas
    // casser les appelants, mais elle n'affiche aucun toast.
    loadSuccess: (_entityName?: string) => {
      /* no-op : pas de toast au chargement réussi */
    },

    loadError: (entityName: string, error?: string | Error) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors du chargement des ${entityName.toLowerCase()}: ${errorMsg}`);
    },

  // Create
    createSuccess: (entityName: string) => {
      this.success(`${entityName} créé(e) avec succès`);
    },

    createError: (entityName: string, error?: string | Error) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors de la création: ${errorMsg}`);
    },

  // Update
    updateSuccess: (entityName: string) => {
      this.success(`${entityName} mis(e) à jour avec succès`);
    },

    updateError: (entityName: string, error?: string | Error) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors de la mise à jour: ${errorMsg}`);
    },

    // Suppression
    deleteSuccess: (entityName: string) => {
      this.success(`${entityName} supprimé(e) avec succès`);
    },

    deleteError: (entityName: string, error?: string | Error) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors de la suppression: ${errorMsg}`);
    },

  // Generic save (create or update)
    saveSuccess: (entityName: string, isUpdate: boolean) => {
      this.success(`${entityName} ${isUpdate ? 'mis(e) à jour' : 'créé(e)'} avec succès`);
    },

    saveError: (entityName: string, error?: string | Error) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors de la sauvegarde: ${errorMsg}`);
    },

    // Toggle visibility/active
    toggleSuccess: (entityName: string, field: string = 'visibilité') => {
      this.success(`${field} de ${entityName.toLowerCase()} mise à jour`);
    },

    toggleError: (entityName: string, field: string = 'visibilité', error?: string | Error) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors de la mise à jour de ${field}: ${errorMsg}`);
    },

  // Reorder
    reorderSuccess: (entityName: string) => {
      this.success(`Ordre des ${entityName.toLowerCase()} enregistré avec succès`);
    },

    reorderError: (entityName: string, error?: string | Error) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors de la réorganisation: ${errorMsg}`);
    },
  };

  // Helpers pour les uploads
  static upload = {
    success: (fileName?: string) => {
      this.success(`Fichier ${fileName ? `"${fileName}"` : ''} uploadé avec succès`);
    },

    error: (error?: string | Error, fileName?: string) => {
      const errorMsg = error instanceof Error ? error.message : error || 'Erreur inconnue';
      this.error(`Erreur lors de l'upload${fileName ? ` de "${fileName}"` : ''}: ${errorMsg}`);
    },

    validation: (message: string) => {
      this.error(`Validation upload: ${message}`);
    },
  };

  // Helpers pour les validations
  static validation = {
    required: (fieldName: string) => {
      this.error(`Le champ "${fieldName}" est requis`);
    },

    invalid: (fieldName: string, reason?: string) => {
      this.error(`Le champ "${fieldName}" est invalide${reason ? `: ${reason}` : ''}`);
    },

    format: (fieldName: string, expectedFormat: string) => {
      this.error(`Le format du champ "${fieldName}" doit être: ${expectedFormat}`);
    },

    size: (fieldName: string, maxSize: string) => {
      this.error(`La taille du fichier "${fieldName}" ne doit pas dépasser ${maxSize}`);
    },

    type: (fieldName: string, allowedTypes: string[]) => {
      this.error(`Le type du fichier "${fieldName}" doit être: ${allowedTypes.join(', ')}`);
    },
  };

  // Helpers for specific operations
  static operation = {
    seedSuccess: (entityName: string) => {
      this.success(`${entityName} de démonstration créé(e)s avec succès !`);
    },

    publishSuccess: (entityName: string) => {
      this.success(`${entityName} publié(e) avec succès`);
    },

    archiveSuccess: (entityName: string) => {
      this.success(`${entityName} archivé(e) avec succès`);
    },

    duplicateSuccess: (entityName: string) => {
      this.success(`${entityName} dupliqué(e) avec succès`);
    },

    exportSuccess: (fileName: string) => {
      this.success(`Export "${fileName}" généré avec succès`);
    },

    importSuccess: (fileName: string) => {
      this.success(`Import de "${fileName}" effectué avec succès`);
    },

    syncSuccess: (entityName: string) => {
      this.success(`Synchronisation des ${entityName.toLowerCase()} terminée`);
    },
  };
}

// Alias pour un usage plus simple
export const toastService = ToastService;

// Export direct methods for compatibility
export const {
  success: toastSuccess,
  error: toastError,
  info: toastInfo,
  warning: toastWarning,
} = ToastService;

// Export du service CRUD pour usage direct
export const {
  crud: toastCrud,
  upload: toastUpload,
  validation: toastValidation,
  operation: toastOperation,
} = ToastService;
